import { useEffect, useRef, useState } from 'react';
import { usePreferences } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { ICON, Listen, Pause, Play, Stop } from '../icons.ts';
import { readingFor, type Reading } from '../media/read-aloud.ts';

type Status =
  | { status: 'idle' }
  | { status: 'preparing' }
  | { status: 'playing' | 'paused'; part: number; parts: number; selection: boolean; device: boolean }
  | { status: 'error'; message: string };

const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];

/**
 * Listen to an item's text: all of it, or the selected part when `range`
 * gives one. Uses the workspace's speech service, or this device's own voice
 * when none is set up. Viewers can listen; the speed starts from the
 * person's setting.
 */
export function ReadAloudControl({
  ws,
  itemId,
  range,
  beforeStart,
}: {
  ws: string;
  itemId: string;
  /** Character offsets of selected text (as the server counts them), or null for all of it. */
  range?: () => { start: number; end: number } | null;
  /** Runs first, e.g. saving pending edits so what is read is what is on screen. */
  beforeStart?: () => Promise<void>;
}) {
  const api = useApi();
  const preferences = usePreferences(ws);
  const [status, setStatus] = useState<Status>({ status: 'idle' });
  const [rate, setRate] = useState<number | null>(null);
  const reading = useRef<Reading | null>(null);
  const speed = rate ?? preferences.data?.readAloud.speed ?? 1;

  useEffect(() => () => reading.current?.stop(), []);

  const listen = async () => {
    reading.current?.stop();
    const selected = range?.() ?? null;
    setStatus({ status: 'preparing' });
    try {
      await beforeStart?.();
      const plan = await api.readAloud(ws, itemId, selected ?? undefined);
      const device = plan.provider === 'browser';
      const player = readingFor(plan, speed, {
        onPart: (part) => setStatus({ status: 'playing', part, parts: plan.parts.length, selection: Boolean(selected), device }),
        onEnd: () => setStatus({ status: 'idle' }),
        onError: (message) => setStatus({ status: 'error', message }),
      });
      if (!player) {
        setStatus({ status: 'error', message: 'This browser cannot read aloud, and no speech service is set up.' });
        return;
      }
      reading.current = player;
      player.start();
    } catch (error) {
      setStatus({ status: 'error', message: (error as Error).message });
    }
  };

  const active = status.status === 'playing' || status.status === 'paused';
  return (
    <div className="listen-bar" role="group" aria-label="Read aloud">
      {!active && (
        <button className="button button-inline" onClick={() => void listen()} disabled={status.status === 'preparing'}>
          <Listen size={ICON.inline} stroke={ICON.stroke} />
          {status.status === 'preparing' ? 'Preparing…' : 'Listen'}
        </button>
      )}
      {active && (
        <>
          {status.status === 'playing' ? (
            <button
              className="button button-inline"
              onClick={() => {
                reading.current?.pause();
                setStatus({ ...status, status: 'paused' });
              }}
            >
              <Pause size={ICON.inline} stroke={ICON.stroke} />
              Pause
            </button>
          ) : (
            <button
              className="button button-inline"
              onClick={() => {
                reading.current?.resume();
                setStatus({ ...status, status: 'playing' });
              }}
            >
              <Play size={ICON.inline} stroke={ICON.stroke} />
              Resume
            </button>
          )}
          <button
            className="button button-inline"
            onClick={() => {
              reading.current?.stop();
              setStatus({ status: 'idle' });
            }}
          >
            <Stop size={ICON.inline} stroke={ICON.stroke} />
            Stop
          </button>
        </>
      )}
      <label className="listen-speed">
        <span className="meta">Speed</span>
        <select
          className="select select-inline"
          aria-label="Reading speed"
          value={speed}
          onChange={(event) => {
            const next = Number(event.target.value);
            setRate(next);
            reading.current?.setRate(next);
          }}
        >
          {SPEEDS.map((value) => (
            <option key={value} value={value}>
              {value}×
            </option>
          ))}
        </select>
      </label>
      {active && (
        <span className="meta" role="status">
          {status.selection ? 'Reading the selected text' : 'Reading'}
          {status.parts > 1 ? `, part ${status.part + 1} of ${status.parts}` : ''}
          {status.device ? " (by this device's voice)" : ''}
        </span>
      )}
      {status.status === 'error' && (
        <span role="alert">
          <span className="state">Not read aloud.</span> {status.message}
        </span>
      )}
    </div>
  );
}
